// src/stores/userStore.ts
import { defineStore } from 'pinia'
import { ref } from 'vue'
import { jwtDecode } from 'jwt-decode'

// 定义用户信息类型（按需扩展）
export interface UserInfo {
    userId: number;
    username: string;
    avatarUrl: string;
    roles: string[];
    perms: string[];
    token: string;
}

// jwt常量
const JWT_CONFIG = {
    EXPIRATION_WARNING_SECOND: 300,
    EXP_KEY: 'exp'
}

// 本地存储的 key 常量
const STORAGE_KEY = {
    TOKEN: 'user_token',
    USER_INFO: 'user_info'
}

export const useUserStore = defineStore('user', () => {
    // 1. 状态定义（初始值为空）
    const token = ref<string>('')
    const userInfo = ref<UserInfo>({
        userId: 0,
        username: '',
        avatarUrl: '',
        roles: [],
        perms: [],
        token: ''
    })

    // 2. 初始化状态（增加无效token自动清理）
    const initState = () => {
        // 恢复 Token
        const localToken = localStorage.getItem(STORAGE_KEY.TOKEN)
        if (localToken) {
            // 先校验token是否过期，过期则直接清除
            if (isTokenExpiredByToken(localToken)) {
                localStorage.removeItem(STORAGE_KEY.TOKEN)
                localStorage.removeItem(STORAGE_KEY.USER_INFO)
                token.value = ''
                userInfo.value = {
                    userId: 0,
                    username: '',
                    avatarUrl: '',
                    roles: [],
                    perms: [],
                    token: ''
                }
                return
            }
            // 未过期才赋值
            token.value = localToken
        }

        // 恢复 UserInfo（仅当token有效时）
        if (token.value) {
            const localUserInfo = localStorage.getItem(STORAGE_KEY.USER_INFO)
            if (localUserInfo) {
                try {
                    userInfo.value = JSON.parse(localUserInfo)
                } catch (e) {
                    userInfo.value = {
                        userId: 0,
                        username: '',
                        avatarUrl: '',
                        roles: [],
                        perms: [],
                        token: ''
                    }
                    localStorage.removeItem(STORAGE_KEY.USER_INFO)
                }
            }
        }
    }

    /**
     * 新增：单独校验某个token是否过期（用于initState时提前判断）
     */
    const isTokenExpiredByToken = (checkToken: string) => {
        if (!checkToken) return true
        try {
            const decoded: any = jwtDecode(checkToken)
            const expireTime = decoded[JWT_CONFIG.EXP_KEY]
            const currentTime = Math.floor(Date.now() / 1000)
            return currentTime > expireTime
        } catch (e) {
            console.error('解析token过期时间失败:', e)
            return true
        }
    }

    /**
     * 设置 Token（同步到 localStorage）
     */
    const setToken = (newToken: string) => {
        token.value = newToken
        localStorage.setItem(STORAGE_KEY.TOKEN, newToken)
    }

    /**
     * 设置用户信息（同步到 localStorage）
     */
    const setUserInfo = (info: UserInfo) => {
        userInfo.value = info
        localStorage.setItem(STORAGE_KEY.USER_INFO, JSON.stringify(info))
    }

    /**
     * 清除 Token 和用户信息
     */
    const clearToken = () => {
        token.value = ''
        userInfo.value = {
            userId: 0,
            username: '',
            avatarUrl: '',
            roles: [],
            perms: [],
            token: ''
        }
        localStorage.removeItem(STORAGE_KEY.TOKEN)
        localStorage.removeItem(STORAGE_KEY.USER_INFO)
    }

    /**
     * 解析JWT的payload，获取token的过期时间戳(秒级)
     */
    const getTokenExpireTime = () => {
        if (!token.value) return null
        try {
            const decoded: any = jwtDecode(token.value)
            return decoded[JWT_CONFIG.EXP_KEY]
        } catch (e) {
            console.error('解析token过期时间失败:', e)
            return null
        }
    }

    /**
     * 判断token是否【即将过期】
     */
    const isTokenWillExpire = () => {
        const expireTime = getTokenExpireTime()
        if (!expireTime) return false
        const currentTime = Math.floor(Date.now() / 1000)
        return currentTime + JWT_CONFIG.EXPIRATION_WARNING_SECOND >= expireTime
    }

    /**
     * 判断token是否【已经过期】
     */
    const isTokenExpired = () => {
        const expireTime = getTokenExpireTime()
        if (!expireTime) return false
        const currentTime = Math.floor(Date.now() / 1000)
        return currentTime > expireTime
    }

    /**
     * 获取当前token是否有效（未过期+有值）
     */
    const isTokenValid = () => {
        return !!token.value && !isTokenExpired()
    }

    // 导出状态和方法
    return {
        token,
        userInfo,
        initState,
        setToken,
        setUserInfo,
        clearToken,
        getTokenExpireTime,
        isTokenWillExpire,
        isTokenExpired,
        isTokenValid
    }
})